
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Calendar, TrendingUp, Users, Clock } from "lucide-react";
import StudentLayout from "@/components/StudentLayout";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { createApiUrl } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface AttendanceRecord {
  id: string;
  student_id: string;
  student_name: string;
  teacher_id: string;
  teacher_name: string;
  date: string;
  status: string;
  class_type: string;
  marked_at: string;
}

interface WeeklySummary {
  week_start: string;
  week_end: string;
  scheduled_classes: number;
  attended: number;
  remaining: number;
}

const StudentAttendance = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceRecord[]>([]);
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAttendanceData();
  }, []);

  const fetchAttendanceData = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/login');
        return;
      }

      // Fetch attendance history
      const historyResponse = await fetch(createApiUrl('/student/attendance-history'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      // Fetch weekly summary
      const summaryResponse = await fetch(createApiUrl('/student/attendance-summary/week'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (historyResponse.ok && summaryResponse.ok) {
        const historyData = await historyResponse.json();
        const summaryData = await summaryResponse.json();
        
        setAttendanceHistory(historyData);
        setWeeklySummary(summaryData);
      } else if (historyResponse.status === 401 || summaryResponse.status === 401) {
        localStorage.removeItem('studentData');
        localStorage.removeItem('accessToken');
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
        navigate('/login');
      } else {
        throw new Error('Failed to fetch attendance data');
      }
    } catch (error) {
      console.error('Attendance fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load attendance data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">Loading attendance data...</p>
          </div>
        </div>
      </StudentLayout>
    );
  }

  const calculateOverallAttendance = () => {
    if (attendanceHistory.length === 0) return 0;
    const presentCount = attendanceHistory.filter(record => record.status.toLowerCase() === 'present').length;
    return Math.round((presentCount / attendanceHistory.length) * 100);
  };

  const weeklyAttendanceRate = weeklySummary 
    ? Math.round((weeklySummary.attended / weeklySummary.scheduled_classes) * 100) || 0
    : 0;

  return (
    <StudentLayout>
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Attendance History</h1>
          <p className="text-gray-600 text-sm sm:text-base">View your attendance records and statistics</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Week</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{weeklyAttendanceRate}%</div>
              <p className="text-xs text-muted-foreground">
                {weeklySummary?.attended || 0}/{weeklySummary?.scheduled_classes || 0} classes attended
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overall</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-green-600">{calculateOverallAttendance()}%</div>
              <p className="text-xs text-muted-foreground">
                {attendanceHistory.filter(r => r.status.toLowerCase() === 'present').length}/{attendanceHistory.length} total classes
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Classes</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-purple-600">{attendanceHistory.length}</div>
              <p className="text-xs text-muted-foreground">Classes recorded</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Remaining</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-orange-600">{weeklySummary?.remaining || 0}</div>
              <p className="text-xs text-muted-foreground">Classes this week</p>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Progress */}
        {weeklySummary && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Weekly Progress</CardTitle>
              <p className="text-xs sm:text-sm text-gray-600 break-words">
                Week of {new Date(weeklySummary.week_start).toLocaleDateString()} - {new Date(weeklySummary.week_end).toLocaleDateString()}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>Attended: {weeklySummary.attended}</span>
                  <span>Scheduled: {weeklySummary.scheduled_classes}</span>
                </div>
                <Progress value={weeklyAttendanceRate} className="h-3" />
                <p className="text-xs sm:text-sm text-gray-600">
                  {weeklySummary.remaining} classes remaining this week
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed Records */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Attendance Records</CardTitle>
            <p className="text-xs sm:text-sm text-gray-600">Your complete attendance history</p>
          </CardHeader>
          <CardContent>
            {attendanceHistory.length > 0 ? (
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs sm:text-sm">Date</TableHead>
                        <TableHead className="text-xs sm:text-sm">Status</TableHead>
                        <TableHead className="text-xs sm:text-sm">Teacher</TableHead>
                        <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Class Type</TableHead>
                        <TableHead className="text-xs sm:text-sm hidden md:table-cell">Marked At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attendanceHistory.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="text-xs sm:text-sm">{new Date(record.date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              record.status.toLowerCase() === 'present' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {record.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-none">{record.teacher_name}</TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {record.class_type}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-gray-600 hidden md:table-cell">
                            {new Date(record.marked_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">No Attendance Records</h3>
                <p className="text-gray-500 text-sm sm:text-base">
                  Your attendance records will appear here once your teacher starts marking attendance.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </StudentLayout>
  );
};

export default StudentAttendance;
